import Reflux from "reflux";
import { fromJS } from "immutable";

import GroupActions from "../actions/GroupActions";
import UIActions from "@/actions/UIActions";
import { DEFAULT_MODE, GROUP_FOCUS_MODE } from "@/constants/UIConstants";
import BlockUtils from "@/utils/BlockUtils";
import SelectionStore from "./SelectionStore";
import SiteActions from "@/actions/SiteActions";
import SiteDesignStore from "./SiteDesignStore";
import IDUtils from "@/utils/IDUtils";

/**
 * @typedef {import("@/types/ElementTypes.mjs").DimensionPosition} DimensionPosition
 * @typedef {typeof import("@/constants/DesignConstants.mjs").LAYOUTS[number]} Layout
 */

const GroupStore = Reflux.createStore({
  storeName: 'GroupStore',
  listenables: [GroupActions],

  init() {
    this.state = fromJS({
      blockId: null,
      groupId: null,
      groupSelectionId: null,
      groupElementData: null,
      layout: null,

      groupMatrix: null,

      // initial selection
      initialSelectionCoordinates: null,

      // might not be necessary since I think we'd only use this during a focus state
      focus: false,
      holdFocus: false,

      transformerElement: null,
      transforming: false,
    });

    [SiteActions.undo, SiteActions.redo].forEach((action) =>
      action.listen(() => this.isFocused() && Promise.resolve().then(this._handleUndoRedo))
    );
  },

  _handleUndoRedo() {
    setTimeout(() => {
      const selected = SiteDesignStore.getSelected();
      const blockContainer = SiteDesignStore.getBlockContainer();
      const block = blockContainer.getBlockEntity(selected.get("blockId"));
      const selectionId = IDUtils.getArray(this.getGroupSelectionId());
      const elementContainer = block.getElementContainerEntity(selectionId);
      const layout = this.state.get("layout");

      this.onUpdateGroupData(elementContainer.data.get(layout), layout);
    }, 10);
  },

  _resetData() {
    this.state = this.state
      .set("blockId", null)
      .set("groupId", null)
      .set("groupSelectionId", null)
      .set("groupElementData", null)
      .set("focus", false)
      .set("initialSelectionCoordinates", null)
      .set("layout", null);
    this.trigger(this.state);
  },

  onBlurGroup() {
    const hold = this.state.get("holdFocus");
    if (!hold) {
      this._resetData();
      UIActions.setMode(DEFAULT_MODE);
    } else {
      this.state = this.state.set("holdFocus", false);
    }
  },

  onFocusGroup({ groupSelectionId, selectedBlock, layout, focusCoordinates }) {
    if (!selectedBlock) {
      selectedBlock = SelectionStore.getBlock();
    }

    const selectedGroupElement = BlockUtils.getBlockElement(selectedBlock, groupSelectionId);

    this.state = this.state
      .set("blockId", selectedBlock.get("id"))
      .set("groupId", selectedGroupElement?.get("id"))
      .set("groupSelectionId", groupSelectionId)
      .set("groupElementData", selectedGroupElement)
      .set("focus", true)
      .set("layout", layout);

    if (focusCoordinates) {
      this.state = this.state.set("initialSelectionCoordinates", focusCoordinates);
    } else {
      // Clear any pending selection coordinates when focusing programmatically
      this.state = this.state.set("initialSelectionCoordinates", null);
    }

    this.trigger(this.state);

    UIActions.setMode(GROUP_FOCUS_MODE);
  },

  getElementDimensions(layout) {
    return this.state.getIn(["groupElementData", layout]);
  },

  getState() {
    return this.state;
  },

  isFocused() {
    return this.state.get("focus");
  },

  getGroupId() {
    return this.state.get("groupId");
  },

  getGroupSelectionId() {
    return this.state.get("groupSelectionId");
  },

  getBlockId() {
    return this.state.get("blockId");
  },

  pendingSelection() {
    return !!this.state.get("initialSelectionCoordinates");
  },

  getPendingSelectionCoords() {
    return this.state.get("initialSelectionCoordinates");
  },

  /**
   * Updates a group's tracked dimensions for a layout. No-op if the group
   * was blurred by the time the update lands (groupElementData is gone —
   * _resetData already triggered with the reset state).
   * @param {import("@/types/ElementTypes.mjs").DimensionPosition} data
   * @param {Layout} layout
   */
  onUpdateGroupData(data, layout) {
    const merged = this.state.getIn(["groupElementData", layout])?.merge(data);
    if (!merged) return;

    this.state = this.state.setIn(["groupElementData", layout], merged);
    this.trigger(this.state);
  },

  // ignores next blur
  onHoldFocus() {
    this.state = this.state.set("holdFocus", true);
  },

  collectPendingSelection() {
    const coords = GroupStore.getPendingSelectionCoords();
    // Allows the selection layer to populate group children into the dom so we have something to find.
    Promise.resolve().then(() => {
      const selectedElem = document
        .elementsFromPoint(coords.x, coords.y)
        .find(
          (htmlRef) =>
            htmlRef.classList.contains("selectable") &&
            htmlRef.dataset.parentId === this.getGroupId()
        );

      // select pending element under mouse coords now that selection layer has rerendered.
      if (selectedElem) {
        const { id, blockId, type } = selectedElem.dataset;

        UIActions.itemOut();
        UIActions.selectElement({
          id,
          type,
          layout: this.state.get("layout"),
          blockId,
          from: "stage",
        });
      }

      this.state = this.state.set("initialSelectionCoordinates", null);
    });
  },
});

export default GroupStore;
